// Pure mapping: storm intensity (0..1) -> visual params.
//
// Particle-affecting params are BUCKETED into tiers because a particle layer that
// rebuilds its WebGL scene on prop changes (the retired ogl <Particles>) must not thrash.
// A continuous slider must not thrash that, so the scene only rebuilds when the
// tier flips. Cheap params (opacities, vignette) stay continuous and are applied
// via CSS, so they cost nothing per frame.

export interface StormParams {
  particleCount: number;
  particleSpeed: number;
  particleBaseSize: number;
  staticOpacity: number; // opacity of the TV-static video, 0..1
  scanlineOpacity: number; // 0..1
  vignette: number; // edge darkening / pull-in, 0..1
}

export function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

// Three tiers keep WebGL scene rebuilds to at most two across the whole range.
const PARTICLE_TIERS = [
  { count: 300, speed: 1.5, baseSize: 70 },
  { count: 600, speed: 3, baseSize: 90 },
  { count: 1000, speed: 5, baseSize: 110 },
] as const;

export function particleTier(intensity: number): number {
  const i = clamp01(intensity);
  return i < 0.34 ? 0 : i < 0.67 ? 1 : 2;
}

export function stormParams(intensityRaw: number): StormParams {
  const i = clamp01(intensityRaw);
  const tier = PARTICLE_TIERS[particleTier(i)];
  return {
    particleCount: tier.count,
    particleSpeed: tier.speed,
    particleBaseSize: tier.baseSize,
    staticOpacity: 0.16 + i * 0.5, // 0.16 .. 0.66 — the distorted mp4 is the hero now
    scanlineOpacity: 0.08 + i * 0.3, // 0.08 .. 0.38
    vignette: 0.15 + i * 0.5, // 0.15 .. 0.65
  };
}
